import { faCog, faFile, faFileExport, faPlus } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Checkbox, Drawer, InputNumber, Select, Tooltip } from "antd";
import _ from "lodash";
import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { receiveTrackGeomData, showRailProfileAudit } from "redux/actions";

const { Option } = Select;

const ChartFilters = ({
    title,
    selectedDataTypes,
    setSelectedDataTypes,
    geomHeaders,
    exceedenceSpeed,
    setExceedenceSpeed,
    colorScheme,
    setColorScheme,
    userIsAdmin,
    userConfig,
    setShowCompareConfig,
    setShowExportConfig,
    hasLeftRightAlignedData,
    alignToTrackLeftRight,
    setAlignToTrackLeftRight,
    geometryData,
    geomDataFiles,
    comparisonSession,
    comparisonOffset,
    setComparisonOffset,
    verticalComparisonOffset,
    setVerticalComparisonOffset,
    isBeta,
    showPoints,
    setShowPoints,
    sessionID,
}) => {
    const dispatch = useDispatch();
    const [active, setActive] = useState(false);
    const [selectOpen, setSelectOpen] = useState(false);

    const handleSelect = (e) => {
        setSelectedDataTypes(e);
    };

    const resetTrackGeometryDataLoadedMarkers = () => {
        const _geometryData = [...geometryData];
        let index = 0;
        for (let i = 0; i < geomDataFiles.length; i++) {
            _geometryData[index].loaded = false;
            index += geomDataFiles[i].record_count;
        }
        dispatch(receiveTrackGeomData(_geometryData));
    };

    const orderedDataTypes = useMemo(() => {
        return _.chain(selectedDataTypes)
            .map((t) => geomHeaders.find((h) => h.header_name === t))
            .sortBy((h) => (h.info.order !== null ? h.info.order : h.name))
            .map((h) => h.header_name)
            .value();
    }, [selectedDataTypes, geomHeaders]);

    const comparisonTitle = useMemo(() => {
        if (comparisonSession) {
            const [vehicle, , route] = comparisonSession.names[0].split("-");
            return (
                <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
                    <p>
                        {vehicle} {route}
                    </p>
                </div>
            );
        }
    }, [comparisonSession]);

    const comparisonAdjustment = useMemo(() => {
        return (
            <div style={{ display: "flex", justifyContent: "center", justifyItems: "center" }}>
                <p style={{ padding: "0px 0px 0px 20px" }}>Horizontal adjustment:&nbsp;</p>
                <InputNumber
                    value={comparisonOffset}
                    onChange={setComparisonOffset}
                    min={-25}
                    max={25}
                    step={0.2}
                    formatter={(v) => `${v}m`}
                    parser={(v) => v.replace(/[^-.0-9]/g, "")}
                />
                <p style={{ padding: "0px 0px 0px 20px" }}>Vertical adjustment (divs):&nbsp;</p>
                <InputNumber
                    value={verticalComparisonOffset}
                    onChange={setVerticalComparisonOffset}
                    min={-25}
                    max={25}
                    step={0.05}
                    formatter={(v) => `${v}`}
                    parser={(v) => v.replace(/[^-.0-9]/g, "")}
                />
            </div>
        );
    }, [comparisonOffset, setComparisonOffset, verticalComparisonOffset, setVerticalComparisonOffset]);

    return (
        <>
            <div className="w-full flex justify-center">
                <Button
                    onClick={() => setActive(true)}
                    style={{ marginBottom: 8, width: "fit-content", display: "flex", alignItems: "center", gap: "1rem" }}>
                    <FontAwesomeIcon icon={faCog} /> Open Toolbar
                </Button>
            </div>
            <Drawer
                placement="top"
                visible={active}
                closable={false}
                onClose={() => {
                    setActive(false);
                    setSelectOpen(false);
                }}
                height="auto"
                style={{ background: "transparent", width: "100%" }}
                bodyStyle={{ padding: 0 }}>
                <div
                    className="flex text-no-wrap w-full"
                    style={{ gap: "2rem", width: "100%" }}>
                    <div className="toolbar-parent-container">
                        <div className="toolbar-access">
                            <div className="toolbar-access__title">
                                <p>{title}</p>
                                <div className="widget__Chart__Container__Disclaimer">
                                    <p>
                                        <b>Track geometry data is in an evaluation phase</b>
                                    </p>
                                </div>
                            </div>
                            <Tooltip title={!hasLeftRightAlignedData ? "This session has no left-right aligned geometry" : null}>
                                <Checkbox
                                    className="toolbar-checkbox"
                                    checked={alignToTrackLeftRight}
                                    disabled={!hasLeftRightAlignedData}
                                    onChange={(e) => {
                                        setAlignToTrackLeftRight(e.target.checked);
                                        resetTrackGeometryDataLoadedMarkers();
                                    }}>
                                    Align to track left and right
                                </Checkbox>
                            </Tooltip>
                            {isBeta && (
                                <Checkbox
                                    className="toolbar-checkbox"
                                    checked={showPoints}
                                    onChange={(e) => {
                                        setShowPoints(e.target.checked);
                                        // Only send audit event when enabling (not disabling)
                                        if (e.target.checked && sessionID) {
                                            dispatch(showRailProfileAudit(sessionID));
                                        }
                                    }}>
                                    Show rail profile points
                                </Checkbox>
                            )}

                            <button
                                className="toolbar-button"
                                onClick={() => {
                                    setActive(false);
                                    setSelectOpen(false);
                                }}>
                                <FontAwesomeIcon icon={faCog} />
                                Close Toolbar
                            </button>
                        </div>
                        <div className="wrapper">
                            <div className={active ? "toolbar-container active" : "toolbar-container"}>
                                <div className={active ? "widget-toolbar" : "widget-toolbar closed"}>
                                    <div className="widget-toolbar__item">
                                        <p>Geometry Measurements:</p>
                                        <div className="measurement-container">
                                            <div
                                                className="select-wrapper"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectOpen(!selectOpen);
                                                }}>
                                                <Select
                                                    onChange={handleSelect}
                                                    value={orderedDataTypes}
                                                    mode="multiple"
                                                    showSearch
                                                    allowClear
                                                    placeholder="Please select"
                                                    style={{ width: "100%" }}
                                                    optionFilterProp="children"
                                                    open={selectOpen}>
                                                    {geomHeaders &&
                                                        _.chain(geomHeaders)
                                                            .filter((option) => option.name)
                                                            .filter((h) => !_.isEmpty(h.info))
                                                            .sortBy((h) => (h.info.order !== null ? h.info.order : h.name))
                                                            .map((option, index) => (
                                                                <Option
                                                                    key={index}
                                                                    value={option.header_name}
                                                                    onClick={(e) => e.domEvent.stopPropagation()}>
                                                                    {_.get(option, "info.display_name", option.name)}
                                                                </Option>
                                                            ))
                                                            .value()}
                                                </Select>
                                            </div>
                                            <Button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectOpen(!selectOpen);
                                                }}>
                                                <div className="tool-button">
                                                    <FontAwesomeIcon
                                                        icon={faPlus}
                                                        style={{ fontSize: 16 }}
                                                    />
                                                    Add Measurements
                                                </div>
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="widget-toolbar__item_group">
                                        <div className="widget-toolbar__item">
                                            <p>Exceedence Bounds:</p>
                                            <Select
                                                value={exceedenceSpeed}
                                                onChange={setExceedenceSpeed}
                                                className="ChartTypeSelect">
                                                <Option value="none">Don't Show</Option>
                                                <Option value="line">For line speed</Option>
                                                <Option value={25}>Up to 25 mph</Option>
                                                <Option value={50}>26-50 mph</Option>
                                                <Option value={75}>51-75 mph</Option>
                                                <Option value={100}>76-100 mph</Option>
                                                <Option value={125}>101-125 mph</Option>
                                            </Select>
                                        </div>
                                        <div className="widget-toolbar__item">
                                            {(!!userIsAdmin || !!userConfig["super_admin"]) && (
                                                <div className="widget-tools">
                                                    <p>Tools:</p>
                                                    <div className="widget-tools__item">
                                                        <Button
                                                            block
                                                            onClick={() => setShowCompareConfig(true)}>
                                                            <div className="tool-button">
                                                                <FontAwesomeIcon
                                                                    icon={faFile}
                                                                    style={{ fontSize: 16 }}
                                                                />
                                                                Compare
                                                            </div>
                                                        </Button>
                                                        <Button
                                                            block
                                                            onClick={() => setShowExportConfig(true)}>
                                                            <div className="tool-button">
                                                                <FontAwesomeIcon
                                                                    icon={faFileExport}
                                                                    style={{ fontSize: 16 }}
                                                                />
                                                                Export
                                                            </div>
                                                        </Button>
                                                        <Button
                                                            block
                                                            onClick={() => setColorScheme(colorScheme === "dark" ? "light" : "dark")}>
                                                            {colorScheme === "dark" ? "Light Theme" : "Dark Theme"}
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        className="widget__Chart__Container__Controls__Zoom"
                        hidden={comparisonSession === null}>
                        {comparisonTitle}
                        {comparisonAdjustment}
                    </div>
                </div>
            </Drawer>
        </>
    );
};

export default ChartFilters;
